import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { get } from "src/services/httpClient.service";
import { authenticationService } from "src/services";
import Button from "@mui/material/Button";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import reportDownloadService from "src/services/reportDownload.service";

const SubscriberMonthlyStatusReport = () => {
    const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(false);

    const [downloading, setDownloading] = useState({
    excel: false,
    pdf: false,
});

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const currentDate = new Date();

    const [month, setMonth] = useState<any>(currentDate.getMonth() + 1);
    const [year, setYear] = useState<any>(currentDate.getFullYear());


    const handleDownloadExcel = async () => {
    setDownloading((prev) => ({
        ...prev,
        excel: true,
    }));

    try {
        const headers = [
            "S.No",
            "Customer ID",
            "First Name",
            "Last Name",
            "Username",
            "Phone",
            "User Status",
            "Subscription Start Date",
            "Subscription End Date",
            "Subscription Status",
            "Status For Selected Month",
        ];

        const data = subscribers.map((row: any, index: number) => [
            index + 1,
            row.customerId || "-",
            row.firstName || "-",
            row.lastName || "-",
            row.userName || "-",
            row.phone || "-",
            row.userStatus || "-",
            row.subscriptionStartDate
                ? formatDate(row.subscriptionStartDate)
                : "-",
            row.subscriptionEndDate
                ? formatDate(row.subscriptionEndDate)
                : "-",
            row.subscriptionStatus || "-",
            row.statusForSelectedMonth || "-",
        ]);

        const worksheet = XLSX.utils.aoa_to_sheet([
            headers,
            ...data,
        ]);

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Monthly Status Report"
        );

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(
            blob,
            `Subscriber Monthly Status Report_${month}_${year}.xlsx`
        );
    } catch (error) {
        console.error("Excel download failed:", error);
    } finally {
        setDownloading((prev) => ({
            ...prev,
            excel: false,
        }));
    }
};

const handleDownloadPDF = async () => {
    setDownloading((prev) => ({
        ...prev,
        pdf: true,
    }));

    try {
        const pdfColumns = [
            { field: "sno", headerName: "S.No" },
            { field: "customerId", headerName: "Customer ID" },
            { field: "firstName", headerName: "First Name" },
            { field: "lastName", headerName: "Last Name" },
            { field: "userName", headerName: "Username" },
            { field: "phone", headerName: "Phone" },
            { field: "userStatus", headerName: "User Status" },
            {
                field: "subscriptionStartDate",
                headerName: "Subscription Start Date",
            },
            {
                field: "subscriptionEndDate",
                headerName: "Subscription End Date",
            },
            {
                field: "subscriptionStatus",
                headerName: "Subscription Status",
            },
            {
                field: "statusForSelectedMonth",
                headerName: "Status For Selected Month",
            },
        ];

        const pdfRows = subscribers.map((row: any, index: number) => ({
            sno: index + 1,
            customerId: row.customerId || "-",
            firstName: row.firstName || "-",
            lastName: row.lastName || "-",
            userName: row.userName || "-",
            phone: row.phone || "-",
            userStatus: row.userStatus || "-",

            subscriptionStartDate: row.subscriptionStartDate
                ? formatDate(row.subscriptionStartDate)
                : "-",

            subscriptionEndDate: row.subscriptionEndDate
                ? formatDate(row.subscriptionEndDate)
                : "-",

            subscriptionStatus: row.subscriptionStatus || "-",

            statusForSelectedMonth:
                row.statusForSelectedMonth || "-",
        }));

        const success =
            await reportDownloadService.downloadReport(
                {
                    title: `Subscriber Monthly Status Report - ${month}/${year}`,
                    columns: pdfColumns,
                    rows: pdfRows,
                    fileName: `Subscriber Monthly Status Report_${month}_${year}.pdf`,
                },
                "pdf"
            );

        if (!success) {
            alert("Failed to generate PDF. Please try again.");
        }
    } catch (error) {
        console.error("PDF download failed:", error);
        alert("Error generating PDF. Please try again.");
    } finally {
        setDownloading((prev) => ({
            ...prev,
            pdf: false,
        }));
    }
};


    const fetchSubscribers = async () => {
    setLoading(true);
    try {
        const response = await get(
            `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/subscriber-monthly-status?month=${month}&year=${year}`
        );

        setSubscribers(response?.data?.data || []);
    } catch (error) {
        console.error("Error fetching report:", error);
        setSubscribers([]);
    } finally {
        setLoading(false);
    }
};

   useEffect(() => {
    fetchSubscribers();
}, [month, year]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatAmount = (value) => {
        if (value === null || value === undefined) return "-";
        return Number(value).toFixed(2);
    };

    const formatDate = (date) => {
        if (!date) return "-";

        return new Date(date).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <Box p={2}>
            <Typography variant="h6" mb={2}>
                Subscriber Monthly Status Report
            </Typography>

            <Grid
    container
    spacing={2}
    mb={2}
    alignItems="center"
>
    <Grid item xs={12} sm={3}>
        <FormControl fullWidth size="small">
            <InputLabel>Month</InputLabel>

            <Select
                value={month}
                label="Month"
                onChange={(e) => {
                    setMonth(e.target.value);
                    setPage(0);
                }}
            >
                {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                ].map((name, index) => (
                    <MenuItem
                        key={index + 1}
                        value={index + 1}
                    >
                        {name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>

    <Grid item xs={12} sm={2}>
        <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>

            <Select
                value={year}
                label="Year"
                onChange={(e) => {
                    setYear(e.target.value);
                    setPage(0);
                }}
            >
                {[2024, 2025, 2026, 2027, 2028].map((y) => (
                    <MenuItem key={y} value={y}>
                        {y}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>

    {/* Download Buttons */}
    <Grid item xs={12} sm={7}>
        <Box
            sx={{
                display: "flex",
                justifyContent: {
                    xs: "flex-start",
                    sm: "flex-end",
                },
                gap: 1,
            }}
        >
            <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
                disabled={
                    downloading.excel ||
                    subscribers.length === 0
                }
            >
                {downloading.excel
                    ? "Generating..."
                    : "Download Excel"}
            </Button>

            <Button
                variant="outlined"
                color="secondary"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadPDF}
                disabled={
                    downloading.pdf ||
                    subscribers.length === 0
                }
            >
                {downloading.pdf
                    ? "Generating..."
                    : "Download PDF"}
            </Button>
        </Box>
    </Grid>
</Grid>
            <Paper elevation={3}
                sx={{
                    backgroundColor: "transparent",
                }}>
                <TableContainer  sx={{
        height: 500, // fixed height
        maxHeight: 500,
    }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>S.No</TableCell>
                                <TableCell>Customer ID</TableCell>
                                <TableCell>First Name</TableCell>
                                <TableCell>Last Name</TableCell>
                                <TableCell>User Name</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>User Status</TableCell>
                                <TableCell>Subscription Start Date</TableCell>
                                <TableCell>Subscription End Date</TableCell>
                                <TableCell>Subscription Status</TableCell>
                                <TableCell>Status For Selected Month</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : subscribers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center">
                                        No Records Found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                subscribers
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>

                                            <TableCell>{row.customerId}</TableCell>

                                            <TableCell>{row.firstName || "-"}</TableCell>

                                            <TableCell>{row.lastName || "-"}</TableCell>

                                            <TableCell>{row.userName || "-"}</TableCell>

                                            <TableCell>{row.phone || "-"}</TableCell>

                                            <TableCell>{row.userStatus || "-"}</TableCell>

                                            <TableCell>
                                                {formatDate(row.subscriptionStartDate)}
                                            </TableCell>

                                            <TableCell>
                                                {formatDate(row.subscriptionEndDate)}
                                            </TableCell>

                                            <TableCell>
                                                {row.subscriptionStatus || "-"}
                                            </TableCell>

                                            <TableCell>
                                                {row.statusForSelectedMonth || "-"}
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={subscribers.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Paper>
        </Box>
    );
};

export default SubscriberMonthlyStatusReport;
